import * as React from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { AppState } from "../../redux/reducer/rootReducer";
import { SubscriptionFeature } from "../../models/auth/Auth";

export enum SubscriptionFeatureName {
  Orders = "Orders",
  RecentOrders = "Recent Orders",
  Leads = "Leads",
  RecentLeads = "Recent Leads",
  CustomerLink = "CustomerLink",
  LinkInventory = "Link Inventory",
  PriceLevels = "Price Levels",
  DMSPromotions = "DMS Promotions",
  WebPromotions = "Web Promotions",
  Wholegoods = "Wholegoods",
  Parts = "Parts",
  CatalogMatching = "Catalog Matching",
  ChatWidget = "Chat Widget",
  DashboardAnalytics = "Dashboard Analytics",
  TotalOrders = "Total Orders",
  TotalSales = "Total Sales",
  AverageOrder = "Average Order",
  TopSellingProducts = "Top Selling",
  MultiLocation = "Multi Location",
  MultiWebsite = "Multi Website",
  Units = "Units",
  QohByLocation = "QOH By Location",
}

const normalizeFeatureName = (name: string): string =>
  name.replace(/\s+/g, "").toLowerCase();

/**
 * Builds a fast O(1) lookup `Set` of allowed subscription feature names from
 * the raw list stored in redux. Memoised by reference so the same array
 * reference yields the same Set (cheap re-renders).
 */
const buildFeatureSet = (
  features: SubscriptionFeature[] | undefined | null,
): Set<string> => {
  if (!features || features.length === 0) return EMPTY_SET;
  return new Set(features.map((f) => normalizeFeatureName(f.featureName)));
};
const EMPTY_SET: Set<string> = new Set();

let cachedRef: SubscriptionFeature[] | null | undefined;
let cachedSet: Set<string> = EMPTY_SET;
const getFeatureSet = (
  features: SubscriptionFeature[] | undefined | null,
): Set<string> => {
  if (features === cachedRef) return cachedSet;
  cachedRef = features;
  cachedSet = buildFeatureSet(features);
  return cachedSet;
};

/** Pure helper – usable outside React (sagas, thunks, utilities). */
export const hasSubscriptionFeatureAccess = (
  features: SubscriptionFeature[] | undefined | null,
  feature: SubscriptionFeatureName,
): boolean => getFeatureSet(features).has(normalizeFeatureName(feature));

/* -------------------------------------------------------------------------- */
/*  Hooks                                                                     */
/* -------------------------------------------------------------------------- */

/** Returns the raw list from redux (or empty array). */
export const useSubscriptionFeatures = (): SubscriptionFeature[] =>
  useSelector(
    (state: AppState) => state.authReducer.subscriptionFeatures ?? EMPTY_LIST,
  );
const EMPTY_LIST: SubscriptionFeature[] = [];

/** Check a single feature. */
export const useHasSubscriptionFeature = (
  feature: SubscriptionFeatureName,
): boolean => {
  const features = useSubscriptionFeatures();
  return hasSubscriptionFeatureAccess(features, feature);
};

/* -------------------------------------------------------------------------- */
/*  Declarative guards                                                        */
/* -------------------------------------------------------------------------- */

export interface SubscriptionFeatureGuardProps {
  /** A single feature, or list of features. */
  feature: SubscriptionFeatureName | SubscriptionFeatureName[];
  /** When `feature` is an array, require ALL (default: ANY). */
  requireAll?: boolean;
  /** Rendered when access is denied. Defaults to `null`. */
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Wrap any JSX (button, section, menu item, page block, …) to gate it behind
 * one or more subscription features.
 *
 *   <SubscriptionFeatureGuard feature={SubscriptionFeatureName.Leads}>
 *     <LeadsButton />
 *   </SubscriptionFeatureGuard>
 */
export const SubscriptionFeatureGuard: React.FC<
  SubscriptionFeatureGuardProps
> = ({ feature, requireAll = false, fallback = null, children }) => {
  const list = Array.isArray(feature) ? feature : [feature];
  const features = useSubscriptionFeatures();
  const allowed = requireAll
    ? list.every((f) => hasSubscriptionFeatureAccess(features, f))
    : list.some((f) => hasSubscriptionFeatureAccess(features, f));
  return <>{allowed ? children : fallback}</>;
};

/* -------------------------------------------------------------------------- */
/*  Route guard                                                               */
/* -------------------------------------------------------------------------- */

export interface SubscriptionRouteProps {
  feature: SubscriptionFeatureName | SubscriptionFeatureName[];
  requireAll?: boolean;
  /** Where to send the user when they don't have access. */
  redirectTo?: string;
  children: React.ReactElement;
}

/**
 * Wrap a route element to enforce subscription access at the page level:
 *
 *   <Route
 *     path="/Leads"
 *     element={
 *       <SubscriptionRoute feature={SubscriptionFeatureName.Leads}>
 *         <Leads />
 *       </SubscriptionRoute>
 *     }
 *   />
 */
export const SubscriptionRoute: React.FC<SubscriptionRouteProps> = ({
  feature,
  requireAll = false,
  redirectTo = "/NotFound",
  children,
}) => {
  const list = Array.isArray(feature) ? feature : [feature];
  const features = useSubscriptionFeatures();
  const allowed = requireAll
    ? list.every((f) => hasSubscriptionFeatureAccess(features, f))
    : list.some((f) => hasSubscriptionFeatureAccess(features, f));
  return allowed ? children : <Navigate to={redirectTo} replace />;
};
